import type { ReactNode } from 'react';
import { cn } from './cn.js';
import { Icon } from './Icon.js';
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from './primitives/sheet.js';

/**
 * O painel do canto direito: um registro aberto por cima da lista, para ver e
 * corrigir sem sair dela.
 *
 * É o par do `Modal` — a mesma anatomia de cabeçalho (título, descrição, as
 * ações ao lado e o fechar), o mesmo véu e o mesmo foco preso —, ancorado à
 * direita e com a altura inteira da janela. O `Modal` interrompe com uma
 * tarefa no meio da tela; o painel mostra UM registro ao lado da lista de onde
 * ele veio, e a lista continua visível à esquerda.
 *
 * Nasceu na ficha do sinistro (05/10/2026, a pedido do dono): Sinistros ›
 * Sinistros abre a ficha e a correção aqui. Criar continua no `Modal`.
 *
 * **Não é a `SideDrawer`.** A gaveta da mesa não tem véu nem foco preso, para
 * o formulário ao lado continuar preenchível; este painel É a tarefa, e trava
 * o que está atrás como o `Modal` trava.
 *
 * O corpo é quem rola, como no `Modal`, e por isso o `Form` com `steps` e o
 * `DetailSections` cabem aqui: o trilho e o rodapé deles são `sticky` e
 * prendem às bordas do corpo. A faixa de etapas do telefone lê
 * `--form-steps-top`, que aqui é zero.
 *
 * No telefone o painel ocupa a tela inteira.
 *
 * ## O cabeçalho tem os 60px da faixa do topo (22/10/2026)
 *
 * O painel nasce na altura inteira da janela, e o cabeçalho dele fica ao lado
 * da faixa do topo da interna (`h-15`, `border-b border-borda`). Com altura
 * medida pelo padding, a linha de baixo dele caía alguns pixels acima da
 * linha da faixa, e as duas não se encontravam — o painel parecia colado por
 * cima de outra tela. `min-h-15` e a mesma `border-borda` fazem as duas linhas
 * virarem uma. `min-` e não `h-`: no telefone o título longo e as ações quebram
 * linha, e altura fixa os cortaria.
 */
export function SidePanel({
  open,
  onOpenChange,
  title,
  descricao,
  actions,
  closeLabel = 'Fechar',
  width = 'larga',
  children,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  descricao?: ReactNode;
  /** Os botões do registro, ao lado do fechar — "Corrigir", "Arquivar". */
  actions?: ReactNode;
  closeLabel?: string;
  /**
   * `larga` é a da ficha com formulário (o sinistro); `estreita` é a de uma
   * ficha de LEITURA, uma coluna só — a 68rem ela vira uma faixa de texto
   * perdida num painel vazio.
   */
  width?: 'larga' | 'estreita';
  children: ReactNode;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        className={cn(
          'w-full gap-0 border-borda bg-superficie p-0',
          width === 'larga'
            ? 'sm:max-w-[min(68rem,calc(100vw-2rem))]'
            : 'sm:max-w-[min(36rem,calc(100vw-2rem))]',
        )}
      >
        {/* Mesma anatomia de cabeçalho do `Modal` e do `Card`. */}
        <div className="flex min-h-15 shrink-0 flex-wrap items-center gap-x-4 gap-y-2 border-b border-borda px-4 py-2">
          <div className="min-w-0 flex-1">
            <SheetTitle className="text-base font-bold text-texto">{title}</SheetTitle>
            {descricao && (
              <SheetDescription asChild>
                <div className="mt-0.5 text-xs text-texto-suave">{descricao}</div>
              </SheetDescription>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {actions}
            <SheetClose
              aria-label={closeLabel}
              className="inline-flex size-11 items-center justify-center rounded-borda text-texto-suave transition-colors hover:bg-superficie-suave hover:text-texto active:bg-borda-suave"
            >
              <Icon nome="fechar" size={16} />
            </SheetClose>
          </div>
        </div>
        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto bg-fundo px-4 pt-4 pb-4 [--form-steps-top:0px] [--form-veil-inset:1rem] [--form-veil:var(--color-fundo)]">
          {children}
        </div>
      </SheetContent>
    </Sheet>
  );
}
